import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { currentAppLanguage } from "../../utils/appLanguages";
import {
  localizedExerciseName,
  localizedField,
  localizedSubTypeName,
  localizedTypeName,
} from "../../utils/localizedCatalogFields";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  LinearProgress,
  Paper,
  Divider,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import FastForwardIcon from "@mui/icons-material/FastForward";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { sessionexlist } from "../../components/funtionsBD/Mainservice";
import { initializeSocket } from "../../socket";
import Loader from "../../components/Loading/Loader";
import { Alert, Snackbar } from "@mui/material";
import { useBrand } from "../../context/BrandContext";
import defaultAppLogo from "../../assets/smarttt-default-logo.png";
import { resolveClubImageUrl } from "../../utils/resolveClubImageUrl";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import "./CoachExerciseControl.css";
import {
  pickCoachControlTargetIndex,
  TE_STATE_CANCELLED,
  TE_STATE_FINALIZED,
  TE_STATE_PAUSED,
  TE_STATE_STARTED,
} from "../../utils/coachExerciseNavigation";
import { readAutoAdvanceSequences } from "../../utils/trainingSequencePreferences";
import { buildAutoSequenceStartPayload } from "../../utils/liveTrainingSettings";
import { AUTO_SEQUENCE_TRANSITION_PAUSE_SEC } from "../../utils/sequenceTransitionPause";
import {
  playEndExerciseSound,
  playSequenceAlarmFromServer,
  sequenceRemainingFromPayload,
} from "../../utils/exerciseTimerSounds";
import {
  deriveCoachControlIdleTimers,
  emitCoachJoinTrainingSession,
} from "../../coaching/coachExerciseLiveActions";
import { confirmFinalizeExercise } from "../../coaching/confirmFinalizeExercise";
import {
  confirmSkipExercise,
  confirmSkipSequence,
} from "../../coaching/confirmSkipTraining";
import { isKotExercise, isTeSequences } from "../../utils/teExecutionType";
import { usePermissions } from "../../context/Permissioncontext";
import { readUserFromStorage } from "../../utils/planningAccess";
import { isCoachViewForTraining } from "../../utils/trainingRoomAccess";
import { durationMinutesToSeconds } from "../../utils/trainingDuration";
import {
  isStaleExerciseTimerEvent,
  mergeExerciseTimerSnap,
  noteExerciseTimerRevision,
  patchExercisesFromSyncDomains,
  requestSessionSync,
} from "../../utils/sessionSync";

/** Normalize API/socket timer anchors to epoch ms so countdown math stays correct across string vs number payloads. */
function coachControlEpochMs(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const asNum = Number(trimmed);
    if (Number.isFinite(asNum) && asNum > 1e11) return asNum;
    const parsed = Date.parse(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.getTime();
  }
  return null;
}

export const CoachExerciseControl = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const user = readUserFromStorage();
  const canAccessCoachControl = isCoachViewForTraining(user, permissions);

  useEffect(() => {
    if (!canAccessCoachControl) {
      navigate("/training", { replace: true });
    }
  }, [canAccessCoachControl, navigate]);

  const { t, i18n } = useTranslation();
  const { brand } = useBrand();
  const brandPrimary = brand?.color_primary || "#0877BC";
  const brandPrimaryDark = brand?.color_primary_dark || "#065a8f";
  const brandSecondary = brand?.color_secondary || "#46528b";
  const brandName = brand?.brand_short_name || brand?.brand_name || "Smart TT";
  const sessionInfo = useMemo(() => {
    const fromNav = location.state?.session;
    if (fromNav?.ts_id) return fromNav;
    try {
      const raw = JSON.parse(localStorage.getItem("rsessn") || "null");
      return raw?.ts_id ? raw : null;
    } catch {
      return null;
    }
  }, [location.state?.session]);

  const selectedgrp = useMemo(() => {
    const fromNav = location.state?.selectedgrp;
    if (fromNav?.groupDetails) return fromNav;
    try {
      return JSON.parse(localStorage.getItem("grpinf") || "null");
    } catch {
      return null;
    }
  }, [location.state?.selectedgrp]);

  const [exerciseData, setExerciseData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [open, setOpen] = useState(false);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [finalizing, setFinalizing] = useState(false);
  const [startingNextSeq, setStartingNextSeq] = useState(false);
  const [skippingSequence, setSkippingSequence] = useState(false);
  const token = localStorage.getItem("token");
  const socket = initializeSocket(token);
  const currentlng = currentAppLanguage(i18n);
  const exerciseIndexRef = useRef(currentExerciseIndex);
  const exerciseDataRef = useRef(exerciseData);
  /** Apply running/next index once per navigation / session load (not on every list refresh). */
  const coachControlInitialIndexAppliedRef = useRef(false);
  const exerciseTimerRevisionRef = useRef({});
  const coachControlRefreshInFlightRef = useRef(false);
  const coachControlLastRefreshAtRef = useRef(0);
  const cecDebug = useMemo(() => {
    try {
      return String(localStorage.getItem("cec_debug") || "") === "1";
    } catch {
      return false;
    }
  }, []);
  const normalizeCurrentSequenceIndex = useCallback((currentSequence, sequences) => {
    if (currentSequence == null) return null;
    const sequenceCount = Array.isArray(sequences) ? sequences.length : 0;
    const numericSequence = Number(currentSequence);
    if (!Number.isFinite(numericSequence)) return null;
    const normalized = Math.trunc(numericSequence);

    // Backend sends 1-indexed values for display; keep compatibility with any 0-indexed values.
    if (sequenceCount > 0) {
      if (normalized >= 1 && normalized <= sequenceCount) return normalized - 1;
      if (normalized >= 0 && normalized < sequenceCount) return normalized;
      return null;
    }

    return normalized >= 0 ? normalized : null;
  }, []);

  useEffect(() => { exerciseIndexRef.current = currentExerciseIndex; }, [currentExerciseIndex]);
  useEffect(() => { exerciseDataRef.current = exerciseData; }, [exerciseData]);

  useEffect(() => {
    coachControlInitialIndexAppliedRef.current = false;
  }, [sessionInfo?.ts_id, location.key]);

  const autoAdvanceToNext = useCallback((finalizedTeId) => {
    const list = exerciseDataRef.current;
    const idx = exerciseIndexRef.current;
    const current = list[idx];
    if (!current || current.te_id !== finalizedTeId) return;
    if (idx < list.length - 1) {
      setTimeout(() => setCurrentExerciseIndex(idx + 1), 1500);
    }
  }, []);

  useEffect(() => {
    if (!sessionInfo?.ts_id) {
      setLoading(false);
      return undefined;
    }

    getExerciseList(sessionInfo.ts_id);

    socket.on("exerciseStateUpdate", (data) => {
      if (data.error) {
        setError(data.error);
        setOpen(true);
        if (sessionInfo?.ts_id) setTimeout(() => getExerciseList(sessionInfo.ts_id), 500);
        return;
      }
      if (
        data.te_id &&
        isStaleExerciseTimerEvent(data.te_id, data.exercise_timer_revision)
      ) {
        return;
      }
      if (data.te_id && data.exercise_timer_revision != null) {
        noteExerciseTimerRevision(data.te_id, data.exercise_timer_revision);
        exerciseTimerRevisionRef.current[String(data.te_id)] =
          Number(data.exercise_timer_revision) || 0;
      }

      const isFinalizedStrict =
        data.te_state === "finalized" || data.te_state === TE_STATE_FINALIZED;
      const isTerminal =
        isFinalizedStrict || data.te_state === TE_STATE_CANCELLED;
      const isExerciseAlarm = !data.alarmType || data.alarmType === "exercise";
      if (data.alarm && isFinalizedStrict && isExerciseAlarm) {
        playEndExerciseSound(data.te_id);
      }

      if (isTerminal) autoAdvanceToNext(data.te_id);

      setExerciseData((prev) => {
        const exerciseRemaining = data.timer ?? data.remaining_exercise_time_seconds;
        const sequenceRemaining = data.sequenceTimer ?? data.remaining_sequence_time_seconds;
        const nextSeqStart =
          data.sequenceStartTime !== undefined && data.sequenceStartTime !== null
            ? coachControlEpochMs(data.sequenceStartTime)
            : null;
        const nextSyncStart =
          data.synchronizedStartTime !== undefined && data.synchronizedStartTime !== null
            ? coachControlEpochMs(data.synchronizedStartTime)
            : null;
        return prev.map((ex) =>
          ex.te_id === data.te_id
            ? {
                ...ex,
                te_state: data.te_state,
                tbl_state_te: data.te_state ? { st_id: data.te_state } : (ex.tbl_state_te || { st_id: null }),
                timer: exerciseRemaining ?? ex.timer,
                currentSequence: data.currentSequence !== undefined
                  ? normalizeCurrentSequenceIndex(data.currentSequence, ex.sequences)
                  : ex.currentSequence,
                sequenceTimer: sequenceRemaining ?? ex.sequenceTimer,
                sequenceStartTime: nextSeqStart != null ? nextSeqStart : ex.sequenceStartTime,
                synchronizedStartTime: nextSyncStart != null ? nextSyncStart : ex.synchronizedStartTime,
                awaitingManualSequenceStart:
                  data.awaiting_manual_sequence_start != null
                    ? !!data.awaiting_manual_sequence_start
                    : isTerminal
                      ? false
                      : ex.awaitingManualSequenceStart,
                awaitingAutoSequenceStart:
                  data.awaiting_auto_sequence_start != null
                    ? !!data.awaiting_auto_sequence_start
                    : isTerminal
                      ? false
                      : ex.awaitingAutoSequenceStart,
                sequenceTransitionPauseRemaining:
                  data.sequence_transition_pause_remaining != null
                    ? Number(data.sequence_transition_pause_remaining)
                    : isTerminal
                      ? null
                      : ex.sequenceTransitionPauseRemaining,
                nextSequenceNumber:
                  data.next_sequence_number != null
                    ? data.next_sequence_number
                    : isTerminal
                      ? null
                      : ex.nextSequenceNumber,
              }
            : ex
        );
      });
    });

    socket.on("timerUpdate", (data) => {
      if (!data.te_id) return;
      setExerciseData((prev) => {
        if (!prev.find(ex => ex.te_id === data.te_id)) return prev;
        return prev.map((ex) => {
          if (ex.te_id !== data.te_id) return ex;
          const exerciseRemaining = data.timer ?? data.remaining_exercise_time_seconds;
          const sequenceRemaining = data.sequenceTimer ?? data.remaining_sequence_time_seconds;
          const tickSeqStart =
            data.sequenceStartTime !== undefined && data.sequenceStartTime !== null
              ? coachControlEpochMs(data.sequenceStartTime)
              : null;
          const tickSyncStart =
            data.synchronizedStartTime !== undefined && data.synchronizedStartTime !== null
              ? coachControlEpochMs(data.synchronizedStartTime)
              : null;
          const mergedSync =
            tickSyncStart != null ? tickSyncStart : coachControlEpochMs(ex.synchronizedStartTime);
          const mergedSeq =
            tickSeqStart != null ? tickSeqStart : coachControlEpochMs(ex.sequenceStartTime);
          const nextAwait =
            data.awaiting_manual_sequence_start != null
              ? !!data.awaiting_manual_sequence_start
              : ex.awaitingManualSequenceStart;
          const nextAwaitAuto =
            data.awaiting_auto_sequence_start != null
              ? !!data.awaiting_auto_sequence_start
              : ex.awaitingAutoSequenceStart;
          const nextPauseRem =
            data.sequence_transition_pause_remaining != null
              ? Number(data.sequence_transition_pause_remaining)
              : ex.sequenceTransitionPauseRemaining;
          const nextNextNum =
            data.next_sequence_number != null
              ? data.next_sequence_number
              : ex.nextSequenceNumber;
          return {
            ...ex,
            synchronizedStartTime: mergedSync ?? ex.synchronizedStartTime,
            sequenceStartTime: mergedSeq ?? ex.sequenceStartTime,
            currentSequence: data.currentSequence !== undefined
              ? normalizeCurrentSequenceIndex(data.currentSequence, ex.sequences)
              : ex.currentSequence,
            timer:
              exerciseRemaining !== undefined && exerciseRemaining !== null
                ? exerciseRemaining
                : ex.timer,
            sequenceTimer:
              sequenceRemaining !== undefined && sequenceRemaining !== null
                ? sequenceRemaining
                : ex.sequenceTimer,
            awaitingManualSequenceStart: nextAwait,
            awaitingAutoSequenceStart: nextAwaitAuto,
            sequenceTransitionPauseRemaining: nextPauseRem,
            nextSequenceNumber: nextNextNum,
          };
        });
      });
    });

    socket.on("sequenceUpdate", (data) => {
      if (data.alarm && data.alarmType === "sequence") {
        playSequenceAlarmFromServer({
          teId: data.te_id,
          sequenceOrder: data.currentSequence ?? 0,
          remainingSeconds: sequenceRemainingFromPayload(data),
        });
      }
      const sequenceRemaining = data.sequenceTimer ?? data.remaining_sequence_time_seconds;
      const seqUpdStart =
        data.sequenceStartTime !== undefined && data.sequenceStartTime !== null
          ? coachControlEpochMs(data.sequenceStartTime)
          : null;
      const seqUpdSync =
        data.synchronizedStartTime !== undefined && data.synchronizedStartTime !== null
          ? coachControlEpochMs(data.synchronizedStartTime)
          : null;
      setExerciseData((prev) =>
        prev.map((ex) =>
          ex.te_id === data.te_id
            ? {
                ...ex,
                currentSequence: data.currentSequence !== undefined
                  ? normalizeCurrentSequenceIndex(data.currentSequence, ex.sequences)
                  : ex.currentSequence,
                sequenceTimer: sequenceRemaining !== undefined ? sequenceRemaining : data.sequenceTimer,
                sequenceStartTime: seqUpdStart != null ? seqUpdStart : ex.sequenceStartTime,
                synchronizedStartTime: seqUpdSync != null ? seqUpdSync : ex.synchronizedStartTime,
                timer:
                  data.timer !== undefined && data.timer !== null
                    ? data.timer
                    : data.remaining_exercise_time_seconds !== undefined
                      ? data.remaining_exercise_time_seconds
                      : ex.timer,
                awaitingManualSequenceStart:
                  data.awaiting_manual_sequence_start != null
                    ? !!data.awaiting_manual_sequence_start
                    : ex.awaitingManualSequenceStart,
                awaitingAutoSequenceStart:
                  data.awaiting_auto_sequence_start != null
                    ? !!data.awaiting_auto_sequence_start
                    : ex.awaitingAutoSequenceStart,
                sequenceTransitionPauseRemaining:
                  data.sequence_transition_pause_remaining != null
                    ? Number(data.sequence_transition_pause_remaining)
                    : ex.sequenceTransitionPauseRemaining,
                nextSequenceNumber:
                  data.next_sequence_number != null
                    ? data.next_sequence_number
                    : ex.nextSequenceNumber,
              }
            : ex
        )
      );
    });

    socket.on("sequenceOrderChanged", (data) => {
      if (!data.te_id || !data.sequences?.length) return;
      const orderMap = data.sequences.reduce((acc, s) => {
        if (s.tes_id != null) acc[String(s.tes_id)] = s.sequence_order ?? 9999;
        return acc;
      }, {});
      setExerciseData((prev) =>
        prev.map((ex) => {
          if (ex.te_id !== data.te_id || !ex.sequences?.length) return ex;
          const sorted = [...ex.sequences].sort((a, b) => {
            const oa = orderMap[String(a.tes_id)] ?? a.sequence_order ?? 9999;
            const ob = orderMap[String(b.tes_id)] ?? b.sequence_order ?? 9999;
            return oa - ob;
          });
          return { ...ex, sequences: sorted };
        })
      );
    });

    const mergePlanIntoExercise = (ex, data) => {
      if (!ex || ex.te_id !== data.te_id) return ex;
      const next = { ...ex, te_duration: data.te_duration ?? ex.te_duration };
      if (data.sequences?.length && ex.sequences?.length) {
        const byTes = Object.fromEntries(
          data.sequences.map((s) => [String(s.tes_id), s])
        );
        next.sequences = [...ex.sequences]
          .map((seq) => {
            const u = byTes[String(seq.tes_id)];
            if (!u) return seq;
            return {
              ...seq,
              sequence_duration: u.sequence_duration ?? seq.sequence_duration,
              sequence_order: u.sequence_order ?? seq.sequence_order,
            };
          })
          .sort((a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0));
      }
      return next;
    };

    socket.on("exercisePlanUpdated", (data) => {
      if (!data?.te_id) return;
      setExerciseData((prev) => prev.map((ex) => mergePlanIntoExercise(ex, data)));
    });

    socket.on("exerciseOrderUpdated", (data) => {
      if (!data?.exercises?.length) return;
      const orderMap = Object.fromEntries(
        data.exercises.map((e) => [String(e.te_id), e.te_order])
      );
      setExerciseData((prev) =>
        [...prev]
          .map((ex) => ({
            ...ex,
            te_order: orderMap[String(ex.te_id)] ?? ex.te_order,
          }))
          .sort((a, b) => (a.te_order ?? 0) - (b.te_order ?? 0))
      );
    });

    socket.on("getStatusTE", (response) => {
      if (response.error) return;
      const statusData = response.data;
      if (!statusData?.te_id) return;
      const stSeq =
        statusData.sequenceStartTime !== undefined && statusData.sequenceStartTime !== null
          ? coachControlEpochMs(statusData.sequenceStartTime)
          : null;
      const stSync =
        statusData.synchronizedStartTime !== undefined && statusData.synchronizedStartTime !== null
          ? coachControlEpochMs(statusData.synchronizedStartTime)
          : null;
      setExerciseData((prev) =>
        prev.map((ex) =>
          ex.te_id === statusData.te_id
            ? {
                ...ex,
                te_state: statusData.te_state,
                tbl_state_te: statusData.tbl_state_te,
                timer: statusData.timer ?? ex.timer,
                currentSequence: statusData.currentSequence !== undefined
                  ? normalizeCurrentSequenceIndex(statusData.currentSequence, statusData.sequences || ex.sequences)
                  : ex.currentSequence,
                sequenceTimer: statusData.sequenceTimer ?? ex.sequenceTimer,
                sequenceStartTime: stSeq != null ? stSeq : ex.sequenceStartTime,
                synchronizedStartTime: stSync != null ? stSync : ex.synchronizedStartTime,
                sequences: statusData.sequences || ex.sequences,
                awaitingManualSequenceStart:
                  statusData.awaiting_manual_sequence_start != null
                    ? !!statusData.awaiting_manual_sequence_start
                    : ex.awaitingManualSequenceStart,
                awaitingAutoSequenceStart:
                  statusData.awaiting_auto_sequence_start != null
                    ? !!statusData.awaiting_auto_sequence_start
                    : ex.awaitingAutoSequenceStart,
                sequenceTransitionPauseRemaining:
                  statusData.sequence_transition_pause_remaining != null
                    ? Number(statusData.sequence_transition_pause_remaining)
                    : ex.sequenceTransitionPauseRemaining,
                nextSequenceNumber:
                  statusData.next_sequence_number != null
                    ? statusData.next_sequence_number
                    : ex.nextSequenceNumber,
              }
            : ex
        )
      );
    });

    const onTrainingSessionStatusUpdate = (data) => {
      if (!data?.ts_id || data.ts_id !== sessionInfo.ts_id) return;
      getExerciseList(sessionInfo.ts_id);
    };
    socket.on("trainingSessionStatusUpdate", onTrainingSessionStatusUpdate);

    return () => {
      socket.off("exerciseStateUpdate");
      socket.off("timerUpdate");
      socket.off("sequenceUpdate");
      socket.off("sequenceOrderChanged");
      socket.off("exercisePlanUpdated");
      socket.off("exerciseOrderUpdated");
      socket.off("getStatusTE");
      socket.off("trainingSessionStatusUpdate", onTrainingSessionStatusUpdate);
    };
  }, [sessionInfo?.ts_id, normalizeCurrentSequenceIndex]);

  useEffect(() => {
    if (!socket || !sessionInfo?.ts_id) return undefined;
    const rejoin = () => {
      const stored = readUserFromStorage();
      socket.emit("joinTrainingSession", {
        ts_id: sessionInfo.ts_id,
        user_id: stored?.user_id || "coach",
        role: "coach",
      });
    };
    socket.on("connect", rejoin);
    return () => {
      socket.off("connect", rejoin);
    };
  }, [socket, sessionInfo?.ts_id]);

  // Client-side 1-second tick: compute remaining times from authoritative server timestamps
  useEffect(() => {
    const interval = setInterval(() => {
      setExerciseData((prev) => {
        const sessionTs = sessionInfo?.ts_id;
        const useServerOnlyTimersForSequences =
          sessionTs && !readAutoAdvanceSequences(sessionTs);
        let changed = false;
        const updated = prev.map((ex) => {
          const stateId = ex.tbl_state_te?.st_id || ex.te_state;
          const isRunning = stateId === TE_STATE_STARTED || stateId === "started";
          const isFinalized =
            stateId === TE_STATE_FINALIZED || stateId === "finalized";

          if (isFinalized) {
            if (ex.timer !== 0 || ex.sequenceTimer !== 0) { changed = true; return { ...ex, timer: 0, sequenceTimer: 0 }; }
            return ex;
          }
          if (!isRunning) return ex;
          if (ex.awaitingManualSequenceStart) return ex;
          if (ex.sequences?.length > 0 && useServerOnlyTimersForSequences) {
            return ex;
          }

          const now = Date.now();
          let remaining = ex.timer ?? 0;
          let seqRemaining = ex.sequenceTimer ?? 0;

          const syncAnchor = coachControlEpochMs(ex.synchronizedStartTime);
          const seqAnchor = coachControlEpochMs(ex.sequenceStartTime);
          if (syncAnchor != null) {
            const elapsed = Math.floor((now - syncAnchor) / 1000);
            let totalDur = 0;
            if (ex.sequences?.length > 0) {
              totalDur = ex.sequences.reduce((s, sq) => s + (parseInt(sq.sequence_duration) || 0) * 60, 0);
            } else {
              totalDur = durationMinutesToSeconds(ex.te_duration);
            }
            remaining = Math.max(0, totalDur - elapsed);

            const currentSequenceIndex = ex.currentSequence;
            if (
              ex.sequences?.length > 0 &&
              seqAnchor != null &&
              currentSequenceIndex != null &&
              currentSequenceIndex >= 0 &&
              currentSequenceIndex < ex.sequences.length
            ) {
              const seqElapsed = Math.floor((now - seqAnchor) / 1000);
              const seqDur = (ex.sequences[currentSequenceIndex]?.sequence_duration || 0) * 60;
              seqRemaining = Math.max(0, seqDur - seqElapsed);
            }
          } else if (ex.timer != null) {
            remaining = Math.max(0, ex.timer - 1);
            if (ex.sequenceTimer != null && ex.sequenceTimer > 0) seqRemaining = Math.max(0, ex.sequenceTimer - 1);
          }

          changed = true;
          return { ...ex, timer: remaining, sequenceTimer: seqRemaining };
        });
        return changed ? updated : prev;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [normalizeCurrentSequenceIndex, sessionInfo?.ts_id]);

  const getExerciseList = async (ts_id, opts = {}) => {
    try {
      setLoading(true);
      const response = await sessionexlist({ ts_id });
      const raw = response.data.data || [];
      const initializedData = raw.map((ex) => {
        const sequences = (ex.sequences || [])
          .slice()
          .sort((a, b) => (a.sequence_order ?? 9999) - (b.sequence_order ?? 9999));
        const currentSequence = normalizeCurrentSequenceIndex(
          ex.currentSequence,
          sequences
        );
        const merged = {
          ...ex,
          sequences,
          currentSequence,
          timer: ex.timer ?? 0,
          sequenceTimer: ex.sequenceTimer ?? 0,
        };
        const idleTimers = deriveCoachControlIdleTimers(merged);
        if (idleTimers) {
          merged.timer = idleTimers.timer;
          merged.sequenceTimer = idleTimers.sequenceTimer;
        }
        return merged;
      });
      const sortedData = [...initializedData].sort(
        (a, b) => (a.te_order ?? 0) - (b.te_order ?? 0)
      );
      setExerciseData(sortedData);

      const shouldApplyInitial =
        !coachControlInitialIndexAppliedRef.current && sortedData.length > 0;
      const shouldForceAlign = !!opts.forceAlign && sortedData.length > 0;
      if (shouldApplyInitial || shouldForceAlign) {
        if (shouldApplyInitial) coachControlInitialIndexAppliedRef.current = true;
        const fromNav = location.state?.alignCoachControlIndex;
        const rawIx = location.state?.initialExerciseIndex;
        let nextIndex = pickCoachControlTargetIndex(sortedData);
        if (
          shouldApplyInitial &&
          fromNav &&
          typeof rawIx === "number" &&
          Number.isFinite(rawIx)
        ) {
          const max = sortedData.length - 1;
          nextIndex = Math.min(Math.max(0, Math.floor(rawIx)), max);
        }
        if (cecDebug) {
          const reason = shouldApplyInitial ? "initial" : "forceAlign";
          console.groupCollapsed(
            `[CEC debug] pick index (${reason}) ts_id=${ts_id} nextIndex=${nextIndex}`
          );
          try {
            console.log("location.state", location.state);
            console.table(
              sortedData.map((ex, idx) => ({
                idx,
                te_order: ex.te_order,
                te_id: ex.te_id,
                te_state: ex.te_state,
                tbl_state_te_id: ex.tbl_state_te?.st_id,
                ex_name: ex.ex_name,
              }))
            );
          } finally {
            console.groupEnd();
          }
        }
        setCurrentExerciseIndex(nextIndex);
      }

      const user = JSON.parse(localStorage.getItem("user"));
      socket.emit("joinTrainingSession", { ts_id, user_id: user?.user_id || "coach", role: "coach" });
      const activeTe =
        pickCoachControlTargetIndex(sortedData) != null
          ? sortedData[pickCoachControlTargetIndex(sortedData)]?.te_id
          : sortedData[0]?.te_id;
      requestSessionSync(socket, {
        tsId: ts_id,
        activeTeId: activeTe,
        onDomains: (domains) => {
          setExerciseData((prev) => patchExercisesFromSyncDomains(prev, domains));
        },
      });
      sortedData.forEach((ex) => {
        if (ex?.te_id && String(ex.te_id) !== String(activeTe)) {
          socket.emit("getStatus", { te_id: ex.te_id });
        }
      });
      setLoading(false);
    } catch (err) {
      console.error("Error fetching exercises:", err);
      setError(err.message || t("errorLoadingExercises") || "Error loading exercises");
      setLoading(false);
    }
  };

  const refreshAndAlignToLiveExercise = useCallback(async () => {
    const tsId = sessionInfo?.ts_id;
    if (!tsId) return;
    if (coachControlRefreshInFlightRef.current) return;
    const now = Date.now();
    // Avoid storms when both focus + visibilitychange fire.
    if (now - coachControlLastRefreshAtRef.current < 400) return;
    coachControlLastRefreshAtRef.current = now;
    coachControlRefreshInFlightRef.current = true;
    try {
      if (cecDebug) {
        console.log(`[CEC debug] refreshAndAlignToLiveExercise ts_id=${tsId}`);
      }
      await getExerciseList(tsId, { forceAlign: true });
    } finally {
      coachControlRefreshInFlightRef.current = false;
    }
  }, [sessionInfo?.ts_id]);

  useEffect(() => {
    if (!cecDebug) return;
    const cur = exerciseData?.[currentExerciseIndex];
    console.log("[CEC debug] currentExerciseIndex changed", {
      currentExerciseIndex,
      te_id: cur?.te_id,
      te_order: cur?.te_order,
      te_state: cur?.te_state,
      tbl_state_te_id: cur?.tbl_state_te?.st_id,
      ex_name: cur?.ex_name,
    });
  }, [cecDebug, currentExerciseIndex, exerciseData]);

  useEffect(() => {
    if (!sessionInfo?.ts_id) return undefined;

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshAndAlignToLiveExercise();
      }
    };
    const onFocus = () => {
      refreshAndAlignToLiveExercise();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
    };
  }, [sessionInfo?.ts_id, refreshAndAlignToLiveExercise]);

  const handleStartExercise = async (te_id) => {
    try {
      console.log("🚀 Starting exercise with sequences:", te_id);
      const joinAck = await emitCoachJoinTrainingSession(
        socket,
        sessionInfo?.ts_id,
        te_id
      );
      if (joinAck?.ok === false && joinAck?.code !== "join_timeout") {
        setError(
          joinAck.code === "socket_disconnected"
            ? t("liveTrainingStartConnectionError") ||
                "Lost connection to the session. Refresh and try Start again."
            : joinAck.code || t("errorStartingExercise") || "Error starting exercise"
        );
        setOpen(true);
        return;
      }
      socket.emit(
        "startWave",
        {
          te_id,
          ...buildAutoSequenceStartPayload(sessionInfo?.ts_id),
        },
        (ack) => {
          if (ack?.ok === false) {
            setError(
              ack.code === "coach_required"
                ? t("liveTrainingCoachRequired") || "Coach session access required."
                : ack.code === "sequence_players_missing"
                  ? t("liveTrainingSequencePlayersMissing") ||
                    "Assign players to all sequences before starting."
                  : ack.error ||
                    ack.code ||
                    t("errorStartingExercise") ||
                    "Error starting exercise"
            );
            setOpen(true);
            return;
          }
          setSuccess(t("exerciseStarted") || "Exercise started");
          setOpen(true);
          setTimeout(() => {
            getExerciseList(sessionInfo.ts_id);
          }, 500);
        }
      );
    } catch (error) {
      console.error("Error starting exercise:", error);
      setError(error.message || t("errorStartingExercise") || "Error starting exercise");
      setOpen(true);
    }
  };

  const handleResumeExercise = async (te_id) => {
    try {
      const ex = exerciseDataRef.current.find((e) => e.te_id === te_id);
      const remainingTime =
        ex?.timer != null && ex.timer !== ""
          ? Math.max(0, Math.floor(Number(ex.timer)))
          : 0;
      socket.emit("resumeWave", {
        te_id,
        remainingTime,
        ...buildAutoSequenceStartPayload(sessionInfo?.ts_id),
      });
      setSuccess(t("exerciseResumed") || "Exercise resumed");
      setOpen(true);
      setTimeout(() => {
        getExerciseList(sessionInfo.ts_id);
      }, 500);
    } catch (error) {
      console.error("Error resuming exercise:", error);
      setError(error.message || t("errorResumingExercise") || "Error resuming exercise");
      setOpen(true);
    }
  };

  const handlePauseExercise = async (te_id) => {
    try {
      socket.emit("pauseWave", { te_id });
      setSuccess(t("exercisePaused") || "Exercise paused");
      setOpen(true);
      setTimeout(() => {
        getExerciseList(sessionInfo.ts_id);
      }, 500);
    } catch (error) {
      console.error("Error pausing exercise:", error);
      setError(error.message || t("errorPausingExercise") || "Error pausing exercise");
      setOpen(true);
    }
  };

  const handleStartNextSequence = (te_id) => {
    if (!te_id || startingNextSeq || finalizing || skippingSequence) return;
    setStartingNextSeq(true);
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      setStartingNextSeq(false);
    };
    const fallbackTimer = setTimeout(finish, 2000);
    socket.emit("startNextTrainingSequence", { te_id }, () => {
      clearTimeout(fallbackTimer);
      finish();
    });
  };

  const handleSkipSequence = (te_id) => {
    if (!te_id || skippingSequence || finalizing || startingNextSeq) return;
    const exercise =
      exerciseData.find((ex) => String(ex.te_id) === String(te_id)) ||
      exerciseData[currentExerciseIndex];
    const currentOneBased =
      exercise?.currentSequence != null
        ? Number(exercise.currentSequence) + 1
        : 1;
    const confirmed = confirmSkipSequence({
      sequences: exercise?.sequences,
      currentSequence: currentOneBased,
      t,
    });
    if (!confirmed) return;
    setSkippingSequence(true);
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      setSkippingSequence(false);
    };
    const fallbackTimer = setTimeout(finish, 2500);
    socket.emit("skipTrainingSequence", { te_id }, (response) => {
      clearTimeout(fallbackTimer);
      finish();
      if (response?.ok === false) {
        setError(
          response.error ||
            response.message ||
            t("error") ||
            "Error skipping sequence"
        );
        setOpen(true);
      }
      if (sessionInfo?.ts_id) getExerciseList(sessionInfo.ts_id);
    });
  };

  const handleSkipExercise = async (te_id) => {
    if (!te_id || finalizing || startingNextSeq || skippingSequence) return;
    const hasNext = currentExerciseIndex < exerciseData.length - 1;
    const confirmed = confirmSkipExercise({ hasNext, t });
    if (!confirmed) return;
    await handleStopExercise(te_id, { skipConfirm: true });
  };

  const handleStopExercise = async (te_id, opts = {}) => {
    if (!te_id || finalizing || startingNextSeq || skippingSequence) return;
    if (!opts.skipConfirm) {
      const exercise =
        exerciseData.find((ex) => String(ex.te_id) === String(te_id)) ||
        exerciseData[currentExerciseIndex];
      const confirmed = confirmFinalizeExercise({
        sequences: exercise?.sequences,
        currentSequence: exercise?.currentSequence,
        awaitingManualSequenceStart: exercise?.awaitingManualSequenceStart,
        t,
      });
      if (!confirmed) return;
    }

    setFinalizing(true);
    try {
      let ackReceived = false;
      const fallbackMs = 2500;
      const fallbackTimer = setTimeout(() => {
        if (!ackReceived && sessionInfo?.ts_id) {
          getExerciseList(sessionInfo.ts_id);
        }
        setFinalizing(false);
      }, fallbackMs);

      socket.emit("finalizeExercise", { te_id }, (response) => {
        ackReceived = true;
        clearTimeout(fallbackTimer);
        setFinalizing(false);
        if (response?.status === "success") {
          setSuccess(response.message || t("exerciseStopped") || "Exercise stopped");
          setOpen(true);
          getExerciseList(sessionInfo.ts_id);
        } else {
          setError(response?.message || t("errorStoppingExercise") || "Error stopping exercise");
          setOpen(true);
          if (sessionInfo?.ts_id) getExerciseList(sessionInfo.ts_id);
        }
      });
    } catch (error) {
      setFinalizing(false);
      setError(error.message || t("errorStoppingExercise") || "Error stopping exercise");
      setOpen(true);
    }
  };

  const handleNextExercise = () => {
    if (currentExerciseIndex < exerciseData.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
    }
  };

  const handlePrevExercise = () => {
    if (currentExerciseIndex > 0) {
      setCurrentExerciseIndex(currentExerciseIndex - 1);
    }
  };

  const handleFinalizeCurrentExercise = async () => {
    if (exerciseData[currentExerciseIndex]) {
      await handleStopExercise(exerciseData[currentExerciseIndex].te_id);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const getStateChipSx = (stateId) => {
    if (stateId === "st202406071447279901" || stateId === "started") {
      return {
        fontWeight: 700,
        bgcolor: "rgba(var(--brand-primary-rgb), 0.18)",
        color: brandPrimary,
        border: "1px solid rgba(var(--brand-primary-rgb), 0.35)",
      };
    }
    if (stateId === "st202406071447279902" || stateId === "paused") {
      return {
        fontWeight: 700,
        bgcolor: "rgba(255, 152, 0, 0.12)",
        color: "#e65100",
        border: "1px solid rgba(255, 152, 0, 0.35)",
      };
    }
    if (stateId === "st202406071447279903" || stateId === "finalized") {
      return {
        fontWeight: 700,
        bgcolor: "rgba(0, 0, 0, 0.06)",
        color: "text.secondary",
        border: "1px solid #e0e0e0",
      };
    }
    if (stateId === TE_STATE_CANCELLED || stateId === "cancelled") {
      return {
        fontWeight: 700,
        bgcolor: "rgba(191, 108, 44, 0.12)",
        color: "#bf6c2c",
        border: "1px dashed rgba(191, 108, 44, 0.45)",
      };
    }
    return { fontWeight: 600, bgcolor: "rgba(0, 0, 0, 0.05)", color: "text.secondary" };
  };

  const getStateLabel = (exercise) => {
    // Check both tbl_state_te?.st_id and te_state as fallback
    const stateId = exercise.tbl_state_te?.st_id || exercise.te_state;
    if (!stateId) {
      const translated = t("notStarted");
      return translated && translated !== "notStarted" ? translated : "Not Started";
    }
    if (stateId === "st202406071447279901" || stateId === "started") return t("running") || "Running";
    if (stateId === "st202406071447279902" || stateId === "paused") return t("paused") || "Paused";
    if (stateId === "st202406071447279903" || stateId === "finalized") return t("finalized") || "Finalized";
    if (stateId === TE_STATE_CANCELLED || stateId === "cancelled") return t("exerciseStateCancelled") || "Cancelled";
    // Try to get localized name from tbl_state_te if available
    return localizedField(exercise.tbl_state_te, "st_name", currentlng) || t("unknown");
  };

  if (!sessionInfo?.ts_id) {
    return (
      <Box
        className="cec-page"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          p: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 420,
            textAlign: "center",
            borderRadius: 3,
            border: `1px solid rgba(var(--brand-primary-rgb), 0.2)`,
          }}
        >
          <FitnessCenterIcon sx={{ fontSize: 48, color: brandPrimary, opacity: 0.4, mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {t("exerciseControl") || "Exercise Control"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("selectSessionForExerciseControl") ||
              "Select a training session from Home to use live exercise control."}
          </Typography>
          <Button variant="contained" onClick={() => navigate("/home")} sx={{ textTransform: "none", fontWeight: 700 }}>
            {t("ae_go_home") || "Back to Home"}
          </Button>
        </Paper>
      </Box>
    );
  }

  if (loading) {
    return <Loader />;
  }

  const navBtnSx = {
    borderColor: "rgba(var(--brand-primary-rgb), 0.45)",
    color: brandPrimary,
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 2,
    "&:hover": {
      borderColor: brandPrimary,
      bgcolor: "rgba(var(--brand-primary-rgb), 0.06)",
    },
    "&.Mui-disabled": {
      borderColor: "rgba(0,0,0,0.12)",
    },
  };

  const groupLabel = selectedgrp?.groupDetails?.gr_name || selectedgrp?.gr_name || "";

  return (
    <>
      <Box
        className="cec-page cec-page--shell"
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: "var(--brand-bg, #f4f5f6)",
        }}
      >
        <Box sx={{ flexShrink: 0, px: { xs: 1, sm: 2 }, pt: 1 }}>
        {/* Hero — brand gradient + session context */}
        <Box
          className="cec-hero cec-hero--compact"
          sx={{
            background: `linear-gradient(135deg, ${brandPrimary} 0%, ${brandPrimaryDark} 52%, ${brandSecondary} 160%)`,
            mb: 1,
          }}
        >
          <Box className="cec-hero-inner">
            <IconButton
              onClick={() => navigate(-1)}
              aria-label="back"
              sx={{
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.12)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <img
                  src={resolveClubImageUrl(brand?.logo_url) || defaultAppLogo}
                  alt=""
                  className="cec-hero-logo"
                />
                <Box>
                  <Typography
                    variant="overline"
                    sx={{ opacity: 0.9, letterSpacing: 1.2, display: "block" }}
                  >
                    {brandName}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.25,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontSize: { xs: "1.05rem", sm: "1.2rem" },
                    }}
                  >
                    <FitnessCenterIcon sx={{ fontSize: 24, opacity: 0.95 }} />
                    {t("exerciseControl") || "Exercise Control"}
                  </Typography>
                </Box>
              </Box>
              {sessionInfo && (
                <Box className="cec-session-meta">
                  {groupLabel && (
                    <span>
                      <strong>{t("group")}</strong> {groupLabel}
                    </span>
                  )}
                  <span>
                    <strong>{t("session")}</strong> {sessionInfo.ts_session_number}
                  </span>
                  <span>
                    {new Date(sessionInfo.ts_date).toLocaleDateString()} ·{" "}
                    {sessionInfo.ts_start_time?.slice(0, 5)}
                  </span>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        </Box>

        <Box
          className="cec-scroll"
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            px: { xs: 1, sm: 2 },
            pb: 2,
          }}
        >
        {/* Exercise Navigation */}
        {exerciseData.length > 1 && (
          <Paper
            elevation={0}
            sx={{
              p: 1.25,
              mb: 1.5,
              maxWidth: 1100,
              width: "100%",
              mx: "auto",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              border: "1px solid rgba(var(--brand-primary-rgb), 0.15)",
              borderRadius: 3,
              bgcolor: "var(--brand-surface, #fff)",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<NavigateBeforeIcon />}
              onClick={handlePrevExercise}
              disabled={currentExerciseIndex === 0}
              sx={navBtnSx}
            >
              {t("previous") || "Previous"}
            </Button>
            <Chip
              label={`${t("exercise")} ${currentExerciseIndex + 1} / ${exerciseData.length}`}
              sx={{
                fontWeight: 700,
                bgcolor: "rgba(var(--brand-primary-rgb), 0.1)",
                color: brandPrimary,
                border: "1px solid rgba(var(--brand-primary-rgb), 0.2)",
              }}
            />
            <Button
              variant="outlined"
              endIcon={<NavigateNextIcon />}
              onClick={handleNextExercise}
              disabled={currentExerciseIndex === exerciseData.length - 1}
              sx={navBtnSx}
            >
              {t("next") || "Next"}
            </Button>
          </Paper>
        )}

        {/* Display only current exercise */}
        {exerciseData.length > 0 && exerciseData[currentExerciseIndex] && (() => {
          const exercise = exerciseData[currentExerciseIndex];
          // Check both te_state (if present) and tbl_state_te.st_id for state
          const stateId = exercise.tbl_state_te?.st_id || exercise.te_state;
          const isRunning = stateId === "st202406071447279901" || stateId === "started";
          const isPaused = stateId === "st202406071447279902" || stateId === "paused";
          const isFinalized = stateId === "st202406071447279903" || stateId === "finalized";
          const isCancelled = stateId === TE_STATE_CANCELLED || stateId === "cancelled";
          const sequenceCount = exercise.sequences?.length || 0;
          const hasSequences = sequenceCount > 0;
          const hasMultipleSequences = sequenceCount > 1;
          const showSequenceUi = hasMultipleSequences && !isCancelled;
          // Calculate total duration: if sequences exist, sum them; otherwise use te_duration (never use sequences when cancelled)
          let totalDurationSeconds = 0;
          if (!isCancelled && hasSequences && exercise.sequences.length > 0) {
            totalDurationSeconds = exercise.sequences.reduce((sum, seq) => sum + (parseInt(seq.sequence_duration) || 0) * 60, 0);
          } else {
            totalDurationSeconds = durationMinutesToSeconds(exercise.te_duration); // Convert minutes to seconds
          }
          const currentTimer = exercise.timer !== undefined && exercise.timer !== null ? exercise.timer : 0;
          const progress = totalDurationSeconds > 0 ? Math.max(0, Math.min(100, ((totalDurationSeconds - currentTimer) / totalDurationSeconds) * 100)) : 0;
          const totalDurationMinutes = Math.floor(totalDurationSeconds / 60);

          /** Before start, highlight sequence 1; when running/paused/finalized, follow currentSequence. */
          const displaySequenceIndex =
            !isRunning && !isPaused && !isFinalized
              ? 0
              : exercise.currentSequence != null &&
                  exercise.currentSequence >= 0 &&
                  exercise.currentSequence < sequenceCount
                ? exercise.currentSequence
                : 0;
          const displaySeqDurationSeconds =
            hasSequences && exercise.sequences[displaySequenceIndex]
              ? (parseInt(exercise.sequences[displaySequenceIndex].sequence_duration, 10) || 0) * 60
              : 0;
          const seqTimerDisplay =
            exercise.sequenceTimer !== undefined && exercise.sequenceTimer !== null
              ? exercise.sequenceTimer
              : 0;
          const seqTimerNumeric = Number(seqTimerDisplay);
          const hasSequenceTimeLeft =
            Number.isFinite(seqTimerNumeric) && seqTimerNumeric > 0;
          /** Between-sequence manual wait: sequence clock at 0. Coach mid-block pause: sequence time still remaining. */
          const showStartNextSequence =
            hasMultipleSequences &&
            !!exercise.awaitingManualSequenceStart &&
            !exercise.awaitingAutoSequenceStart &&
            !hasSequenceTimeLeft;
          const showAutoTransitionPause =
            hasMultipleSequences &&
            !!exercise.awaitingAutoSequenceStart;
          const showSkipSequence =
            isTeSequences(exercise) &&
            (isRunning || isPaused) &&
            !isKotExercise(exercise);
          const showSkipExercise =
            !isRunning && !isPaused && !isFinalized && !isCancelled;
          const skipBusy = finalizing || startingNextSeq || skippingSequence;
          const sequenceProgress = showAutoTransitionPause
            ? Math.max(
                0,
                Math.min(
                  100,
                  ((AUTO_SEQUENCE_TRANSITION_PAUSE_SEC -
                    (exercise.sequenceTransitionPauseRemaining ?? 0)) /
                    AUTO_SEQUENCE_TRANSITION_PAUSE_SEC) *
                    100
                )
              )
            : displaySeqDurationSeconds > 0
              ? Math.max(
                  0,
                  Math.min(
                    100,
                    ((displaySeqDurationSeconds - seqTimerDisplay) /
                      displaySeqDurationSeconds) *
                      100
                  )
                )
              : 0;

          const cardShellSx = {
            width: "100%",
            borderRadius: 3,
            overflow: "hidden",
            border: isRunning
              ? `2px solid rgba(var(--brand-primary-rgb), 0.45)`
              : "1px solid rgba(0,0,0,0.08)",
            boxShadow: isRunning
              ? `0 8px 32px rgba(var(--brand-primary-rgb), 0.18)`
              : "0 4px 24px rgba(0,0,0,0.07)",
          };

          return (
            <Box
              className={`cec-split${showSequenceUi ? " has-sequences" : ""}`}
              sx={{ width: "100%", maxWidth: 1100, mx: "auto" }}
            >
            <Card key={`${exercise.te_id}-main`} sx={cardShellSx}>
              <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 2.5 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="overline" sx={{ color: brandPrimary, fontWeight: 700, letterSpacing: 1 }}>
                          {t("exercise")} {exercise.te_order || currentExerciseIndex + 1}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5, fontFamily: "var(--brand-font), sans-serif" }}>
                          {localizedExerciseName(exercise, currentlng) || (() => {
                            const isCB = exercise.type_ex?.type_name_en === "Blocks of competency";
                            const cbFromExe = exercise.competency_block;
                            const cbName = (row) => localizedField(row, "cb_name", currentlng);
                            const sub = localizedSubTypeName(exercise.sub_type_ex, currentlng);
                            if (isCB) {
                              const fromExe = cbName(cbFromExe);
                              const fromTe =
                                exercise.ex_sub_type_id &&
                                cbName(exercise.ex_sub_type_id);
                              return fromExe || fromTe || sub || "";
                            }
                            const ty = localizedTypeName(exercise.type_ex, currentlng);
                            return [ty, sub].filter(Boolean).join(" - ");
                          })()}
                        </Typography>
                      </Box>
                      <Chip
                        label={getStateLabel(exercise)}
                        size="small"
                        sx={getStateChipSx(stateId)}
                      />
                    </Box>

                    {/* Sequence timer first (multi-sequence): shows first sequence duration before Start */}
                    {showSequenceUi && (
                      <Box
                        sx={{
                          mb: 2.5,
                          p: { xs: 2, sm: 2.5 },
                          borderRadius: 3,
                          background: `linear-gradient(135deg, rgba(var(--brand-primary-rgb), 0.1) 0%, rgba(var(--brand-primary-rgb), 0.03) 100%)`,
                          border: `1px solid rgba(var(--brand-primary-rgb), 0.22)`,
                          borderLeft: `4px solid ${brandSecondary}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ display: "block", textAlign: "center", color: "text.secondary", fontWeight: 700, mb: 0.5 }}
                        >
                          {displaySequenceIndex + 1} / {exercise.sequences.length}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.75,
                            mb: 1,
                            color: "text.secondary",
                          }}
                        >
                          <TimerOutlinedIcon sx={{ fontSize: 22 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8 }}>
                            {showAutoTransitionPause
                              ? t("sequenceTransitionPause") || "Next sequence in"
                              : t("remainingSequenceTime") || "Remaining Sequence Time"}
                          </Typography>
                        </Box>
                        <Typography
                          variant="h4"
                          className="cec-timer-display"
                          sx={{
                            fontWeight: 800,
                            textAlign: "center",
                            mb: 1.25,
                            fontSize: { xs: "1.75rem", sm: "2rem" },
                            color: showAutoTransitionPause
                              ? "#f57c00"
                              : isRunning
                                ? brandSecondary
                                : isPaused
                                  ? "#f57c00"
                                  : brandPrimary,
                            fontFamily: "var(--brand-font), ui-monospace, monospace",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {showAutoTransitionPause
                            ? formatTime(exercise.sequenceTransitionPauseRemaining ?? 0)
                            : formatTime(seqTimerDisplay)}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={sequenceProgress}
                          sx={{
                            height: 12,
                            borderRadius: 6,
                            backgroundColor: "rgba(var(--brand-primary-rgb), 0.12)",
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 6,
                              backgroundColor: isRunning
                                ? brandSecondary
                                : isPaused
                                  ? "#ff9800"
                                  : "rgba(0,0,0,0.25)",
                            },
                          }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 1.5 }}>
                          {Math.floor(displaySeqDurationSeconds / 60)} {t("min") || "min"} · {t("sequence") || "Sequence"}
                        </Typography>
                      </Box>
                    )}

                    {/* Total exercise remaining time */}
                    <Box
                      sx={{
                        mb: 2.5,
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 3,
                        background: `linear-gradient(180deg, rgba(var(--brand-primary-rgb), 0.08) 0%, rgba(var(--brand-primary-rgb), 0.02) 100%)`,
                        border: `1px solid rgba(var(--brand-primary-rgb), 0.2)`,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, mb: 1, color: "text.secondary" }}>
                        <TimerOutlinedIcon sx={{ fontSize: 22 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8 }}>
                          {t("remainingTime") || "Remaining Time"}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h4"
                        className="cec-timer-display"
                        sx={{
                          fontWeight: 800,
                          textAlign: "center",
                          mb: 1.25,
                          fontSize: { xs: "1.75rem", sm: "2rem" },
                          color: isRunning ? brandPrimary : isPaused ? "#f57c00" : "text.secondary",
                          fontFamily: "var(--brand-font), ui-monospace, monospace",
                        }}
                      >
                        {formatTime(currentTimer)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 12,
                          borderRadius: 6,
                          backgroundColor: "rgba(var(--brand-primary-rgb), 0.12)",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 6,
                            backgroundColor: isRunning
                              ? brandPrimary
                              : isPaused
                                ? "#ff9800"
                                : "rgba(0,0,0,0.25)",
                          },
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 1.5 }}>
                        {totalDurationMinutes} {t("min") || "min"} {t("total") || "total"}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2, borderColor: "rgba(var(--brand-primary-rgb), 0.12)" }} />

                    {/* Control Buttons */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <Box className="cec-control-bar">
                        {!isRunning && !isPaused && !isFinalized && !isCancelled && (
                          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, width: "100%" }}>
                          <Button
                            variant="contained"
                            size="large"
                            startIcon={<PlayArrowIcon />}
                            onClick={() => handleStartExercise(exercise.te_id)}
                            sx={{
                              flex: 1,
                              py: 1.5,
                              fontWeight: 800,
                              textTransform: "none",
                              fontSize: "1.05rem",
                              borderRadius: 2,
                              background: `linear-gradient(135deg, ${brandPrimary} 0%, ${brandPrimaryDark} 100%)`,
                              boxShadow: `0 4px 12px rgba(var(--brand-primary-rgb), 0.35)`,
                              "&:hover": {
                                background: `linear-gradient(135deg, ${brandPrimaryDark} 0%, ${brandPrimary} 100%)`,
                                boxShadow: `0 6px 18px rgba(var(--brand-primary-rgb), 0.45)`,
                              },
                            }}
                          >
                            {t("startExercise") || "Start Exercise"}
                          </Button>
                          {showSkipExercise ? (
                            <Button
                              variant="outlined"
                              color="warning"
                              size="large"
                              startIcon={<CancelOutlinedIcon />}
                              onClick={() => handleSkipExercise(exercise.te_id)}
                              disabled={skipBusy}
                              sx={{
                                flex: 1,
                                py: 1.5,
                                fontWeight: 800,
                                textTransform: "none",
                                borderRadius: 2,
                                borderWidth: 2,
                              }}
                            >
                              {t("liveTrainingSkipExercise") || "Skip exercise"}
                            </Button>
                          ) : null}
                          </Box>
                        )}
                        {showStartNextSequence && (
                          <Box sx={{ width: "100%", mb: 1.5 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1, textAlign: "center", fontWeight: 600 }}
                            >
                              {exercise.nextSequenceNumber != null
                                ? (t("awaitingNextSequenceHintNumbered", {
                                    n: exercise.nextSequenceNumber,
                                  }) ||
                                    `Block ${exercise.nextSequenceNumber} is ready when you are.`)
                                : t("awaitingNextSequenceHint") ||
                                  "Current block ended. Start the next sequence when the group is ready."}
                            </Typography>
                            <Button
                              variant="contained"
                              color="success"
                              size="large"
                              startIcon={<PlayArrowIcon />}
                              onClick={() => handleStartNextSequence(exercise.te_id)}
                              disabled={skipBusy}
                              fullWidth
                              sx={{
                                py: 1.5,
                                fontWeight: 800,
                                textTransform: "none",
                                borderRadius: 2,
                              }}
                            >
                              {t("startNextSequence") || "Start next sequence"}
                            </Button>
                            {showSkipSequence ? (
                              <Button
                                variant="outlined"
                                color="warning"
                                size="large"
                                startIcon={<FastForwardIcon />}
                                onClick={() => handleSkipSequence(exercise.te_id)}
                                disabled={skipBusy}
                                fullWidth
                                sx={{
                                  mt: 1.5,
                                  py: 1.5,
                                  fontWeight: 800,
                                  textTransform: "none",
                                  borderRadius: 2,
                                  borderWidth: 2,
                                }}
                              >
                                {t("liveTrainingSkipSequence") || "Skip sequence"}
                              </Button>
                            ) : null}
                          </Box>
                        )}
                        {isRunning && (
                          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
                            <Button
                              variant="outlined"
                              color="warning"
                              size="large"
                              startIcon={<PauseIcon />}
                              onClick={() => handlePauseExercise(exercise.te_id)}
                              sx={{
                                flex: 1,
                                py: 1.5,
                                fontWeight: 800,
                                textTransform: "none",
                                borderRadius: 2,
                                borderWidth: 2,
                              }}
                            >
                              {t("pause") || "Pause"}
                            </Button>
                            {showSkipSequence ? (
                              <Button
                                variant="outlined"
                                color="warning"
                                size="large"
                                startIcon={<FastForwardIcon />}
                                onClick={() => handleSkipSequence(exercise.te_id)}
                                disabled={skipBusy}
                                sx={{ flex: 1, py: 1.5, fontWeight: 800, textTransform: "none", borderRadius: 2, borderWidth: 2 }}
                              >
                                {t("liveTrainingSkipSequence") || "Skip sequence"}
                              </Button>
                            ) : null}
                            <Button
                              variant="contained"
                              color="error"
                              size="large"
                              startIcon={<StopIcon />}
                              onClick={handleFinalizeCurrentExercise}
                              disabled={skipBusy}
                              sx={{ flex: 1, py: 1.5, fontWeight: 800, textTransform: "none", borderRadius: 2 }}
                            >
                              {t("finalize") || "Finalize"}
                            </Button>
                          </Box>
                        )}
                        {isPaused && !showStartNextSequence && !showAutoTransitionPause && (
                          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
                            <Button
                              variant="contained"
                              size="large"
                              startIcon={<PlayArrowIcon />}
                              onClick={() => handleResumeExercise(exercise.te_id)}
                              fullWidth
                              sx={{
                                flex: 1,
                                py: 1.5,
                                fontWeight: 800,
                                textTransform: "none",
                                borderRadius: 2,
                                background: `linear-gradient(135deg, ${brandPrimary} 0%, ${brandPrimaryDark} 100%)`,
                                boxShadow: `0 4px 12px rgba(var(--brand-primary-rgb), 0.35)`,
                                "&:hover": {
                                  background: `linear-gradient(135deg, ${brandPrimaryDark} 0%, ${brandPrimary} 100%)`,
                                },
                              }}
                            >
                              {t("resume") || "Resume"}
                            </Button>
                            {showSkipSequence ? (
                              <Button
                                variant="outlined"
                                color="warning"
                                size="large"
                                startIcon={<FastForwardIcon />}
                                onClick={() => handleSkipSequence(exercise.te_id)}
                                disabled={skipBusy}
                                sx={{ flex: 1, py: 1.5, fontWeight: 800, textTransform: "none", borderRadius: 2, borderWidth: 2 }}
                              >
                                {t("liveTrainingSkipSequence") || "Skip sequence"}
                              </Button>
                            ) : null}
                            <Button
                              variant="contained"
                              color="error"
                              size="large"
                              startIcon={<StopIcon />}
                              onClick={handleFinalizeCurrentExercise}
                              disabled={skipBusy}
                              sx={{ flex: 1, py: 1.5, fontWeight: 800, textTransform: "none", borderRadius: 2 }}
                            >
                              {t("finalize") || "Finalize"}
                            </Button>
                          </Box>
                        )}
                        {isPaused && (showStartNextSequence || showAutoTransitionPause) && (
                          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
                            {showSkipSequence && !showStartNextSequence ? (
                              <Button
                                variant="outlined"
                                color="warning"
                                size="large"
                                startIcon={<FastForwardIcon />}
                                onClick={() => handleSkipSequence(exercise.te_id)}
                                disabled={skipBusy}
                                sx={{ flex: 1, py: 1.5, fontWeight: 800, textTransform: "none", borderRadius: 2, borderWidth: 2 }}
                              >
                                {t("liveTrainingSkipSequence") || "Skip sequence"}
                              </Button>
                            ) : null}
                            <Button
                              variant="contained"
                              color="error"
                              size="large"
                              startIcon={<StopIcon />}
                              onClick={handleFinalizeCurrentExercise}
                              disabled={skipBusy}
                              fullWidth
                              sx={{ flex: 1, py: 1.5, fontWeight: 800, textTransform: "none", borderRadius: 2 }}
                            >
                              {t("finalize") || "Finalize"}
                            </Button>
                          </Box>
                        )}
                        {(isFinalized || isCancelled) && (
                          <>
                            <Chip
                              label={
                                isCancelled
                                  ? t("exerciseStateCancelled") || "Cancelled"
                                  : t("finalized") || "Finalized"
                              }
                              sx={{
                                fontWeight: 700, fontSize: "0.85rem", py: 2.5, px: 2,
                                bgcolor: isCancelled
                                  ? "rgba(191, 108, 44, 0.12)"
                                  : "rgba(0,0,0,0.06)",
                                color: isCancelled ? "#bf6c2c" : "text.secondary",
                                border: isCancelled ? "1px dashed rgba(191, 108, 44, 0.45)" : "none",
                              }}
                            />
                            {currentExerciseIndex < exerciseData.length - 1 && (
                              <Button
                                variant="contained"
                                size="large"
                                endIcon={<NavigateNextIcon />}
                                onClick={handleNextExercise}
                                sx={{
                                  flex: 1, py: 1.5, fontWeight: 800, textTransform: "none", borderRadius: 2,
                                  background: `linear-gradient(135deg, ${brandPrimary} 0%, ${brandPrimaryDark} 100%)`,
                                  boxShadow: `0 4px 12px rgba(var(--brand-primary-rgb), 0.35)`,
                                  "&:hover": { background: `linear-gradient(135deg, ${brandPrimaryDark} 0%, ${brandPrimary} 100%)` },
                                }}
                              >
                                {t("nextExercise") || "Next Exercise"}
                              </Button>
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
              </CardContent>
            </Card>

            {showSequenceUi && (
              <Card
                className="cec-panel-sequences"
                sx={{
                  ...cardShellSx,
                  border: "1px solid rgba(0,0,0,0.08)",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
                }}
              >
                <CardContent
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    maxHeight: { md: "min(520px, calc(100vh - 168px))" },
                    overflowY: "auto",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: "text.primary" }}>
                    {t("sequences") || "Sequences"}
                  </Typography>
                  {exercise.sequences.map((seq, seqIdx) => {
                    const curSeq = exercise.currentSequence;
                    const isActive =
                      isRunning &&
                      !exercise.awaitingManualSequenceStart &&
                      curSeq === seqIdx;
                    const isCompleted =
                      isFinalized ||
                      (curSeq != null && seqIdx < curSeq) ||
                      (exercise.awaitingManualSequenceStart &&
                        curSeq != null &&
                        seqIdx === curSeq);
                    const isUpcoming =
                      curSeq == null ||
                      seqIdx > curSeq ||
                      (exercise.awaitingManualSequenceStart &&
                        curSeq != null &&
                        seqIdx > curSeq);

                    const seqOrder = seq.sequence_order || seqIdx + 1;
                    const isBlocksOfCompetency =
                      exercise.type_ex?.type_name_en === "Blocks of competency";
                    const cbRow = (row) => localizedField(row, "cb_name", currentlng);
                    const seqCbName = cbRow(seq.competency_block);
                    const seqSubTypeName =
                      seqCbName ||
                      localizedSubTypeName(seq.sub_type_ex, currentlng) ||
                      localizedSubTypeName(seq, currentlng) ||
                      cbRow(seq.blocks_compentency) ||
                      "";
                    const exerciseTypeName = localizedTypeName(exercise.type_ex, currentlng);
                    let seqLabel;
                    if (seqSubTypeName) {
                      seqLabel = `${seqOrder}. ${seqSubTypeName}`;
                    } else if (isBlocksOfCompetency && exercise.competency_block) {
                      const cb = cbRow(exercise.competency_block);
                      seqLabel = cb ? `${seqOrder}. ${cb}` : String(seqOrder);
                    } else if (isBlocksOfCompetency && exercise.ex_sub_type_id) {
                      const cb = cbRow(exercise.ex_sub_type_id);
                      seqLabel = cb ? `${seqOrder}. ${cb}` : String(seqOrder);
                    } else if (exerciseTypeName && !isBlocksOfCompetency) {
                      seqLabel = `${seqOrder}. ${exerciseTypeName}`;
                    } else {
                      seqLabel = String(seqOrder);
                    }

                    return (
                      <Paper
                        key={seq.tes_id || seqIdx}
                        elevation={0}
                        className={isActive ? "cec-seq-active" : undefined}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          borderRadius: 2,
                          transition: "all 0.3s ease",
                          opacity: isCompleted ? 0.55 : 1,
                          backgroundColor: isActive
                            ? "rgba(var(--brand-primary-rgb), 0.1)"
                            : isCompleted
                              ? "rgba(0,0,0,0.03)"
                              : "var(--brand-surface, #fff)",
                          border: isActive
                            ? `2px solid ${brandPrimary}`
                            : isCompleted
                              ? "1px solid rgba(0,0,0,0.06)"
                              : "1px solid rgba(0,0,0,0.08)",
                          boxShadow: isActive ? `0 0 0 3px rgba(var(--brand-primary-rgb), 0.15), 0 4px 16px rgba(var(--brand-primary-rgb), 0.12)` : "none",
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                            {isCompleted && (
                              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "success.main", flexShrink: 0 }} />
                            )}
                            {isActive && (
                              <Box
                                className="cec-seq-pulse"
                                sx={{
                                  width: 10, height: 10, borderRadius: "50%",
                                  bgcolor: brandPrimary, flexShrink: 0,
                                }}
                              />
                            )}
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: isActive ? 800 : "bold",
                                textDecoration: isCompleted ? "line-through" : "none",
                                color: isActive ? brandPrimary : isCompleted ? "text.disabled" : "text.primary",
                              }}
                            >
                              {seqLabel}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: "bold", flexShrink: 0,
                              color: isCompleted ? "text.disabled" : "text.primary",
                            }}
                          >
                            {seq.sequence_duration} {t("min") || "min"}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                          <Chip
                            label={`A: ${
                              seq.player_a_role === "active" ? t("performance_role", "Performance")
                                : seq.player_a_role === "sparring" ? t("sparring") || "Sparring"
                                  : t("both") || "Both"
                            }`}
                            size="small"
                            sx={{
                              fontSize: "11px", height: 24, fontWeight: 600,
                              ...(seq.player_a_role === "active"
                                ? { bgcolor: "rgba(var(--brand-primary-rgb), 0.15)", color: brandPrimary }
                                : seq.player_a_role === "both"
                                  ? { bgcolor: "rgba(var(--brand-primary-rgb), 0.08)", color: brandSecondary }
                                  : { bgcolor: "action.hover" }),
                            }}
                          />
                          <Chip
                            label={`B: ${
                              seq.player_b_role === "active" ? t("performance_role", "Performance")
                                : seq.player_b_role === "sparring" ? t("sparring") || "Sparring"
                                  : t("both") || "Both"
                            }`}
                            size="small"
                            sx={{
                              fontSize: "11px", height: 24, fontWeight: 600,
                              ...(seq.player_b_role === "active"
                                ? { bgcolor: "rgba(var(--brand-primary-rgb), 0.15)", color: brandPrimary }
                                : seq.player_b_role === "both"
                                  ? { bgcolor: "rgba(var(--brand-primary-rgb), 0.08)", color: brandSecondary }
                                  : { bgcolor: "action.hover" }),
                            }}
                          />
                        </Box>
                        {isActive && exercise.sequenceTimer !== undefined && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: brandPrimary }}>
                              {t("remaining") || "Remaining"}: {formatTime(exercise.sequenceTimer)}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={(() => {
                                const seqDur = (seq.sequence_duration || 0) * 60;
                                return seqDur > 0 ? Math.min(100, ((seqDur - (exercise.sequenceTimer || 0)) / seqDur) * 100) : 0;
                              })()}
                              sx={{
                                mt: 0.5, height: 4, borderRadius: 2,
                                bgcolor: "rgba(var(--brand-primary-rgb), 0.12)",
                                "& .MuiLinearProgress-bar": { borderRadius: 2, bgcolor: brandPrimary },
                              }}
                            />
                          </Box>
                        )}
                        {isCompleted && (
                          <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "success.main", fontWeight: 600 }}>
                            {t("finalized") || "Finalized"}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })}
                </CardContent>
              </Card>
            )}
            </Box>
          );
        })()}

        {exerciseData.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 5,
              textAlign: "center",
              maxWidth: 560,
              mx: "auto",
              borderRadius: 3,
              border: "1px dashed rgba(var(--brand-primary-rgb), 0.25)",
              bgcolor: "var(--brand-surface, #fff)",
            }}
          >
            <FitnessCenterIcon sx={{ fontSize: 48, color: brandPrimary, opacity: 0.35, mb: 1 }} />
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
              {t("noExercises") || "No exercises available for this session"}
            </Typography>
          </Paper>
        )}
        </Box>
      </Box>

      <Snackbar open={open} autoHideDuration={4000} onClose={() => setOpen(false)}>
        <Alert severity={error ? "error" : "success"} onClose={() => setOpen(false)}>
          {error || success}
        </Alert>
      </Snackbar>
    </>
  );
};

